import { Info } from "lucide-react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import OAButton from "./OAButton";
import Api from "../baseApi";
import { useTunnelData } from "../TunnelContext";
import { CircleAlert } from "lucide-react";
import { useConfig } from "../ConfigProvider";

export default function SubscriptionSummary({ club, offer, options, className, hidePromo = false, style = {} }) {
  const { currency, code: configCode } = useConfig();
  const [promoIsOpen, setPromoIsPromo] = useState(false);
  const [isOpen, setIsPromo] = useState(true);
  const [error, errorSet] = useState("");
  const [codePending, codePendingSet] = useState(false);
  const [code, codeSet] = useState("");
  const { t } = useTranslation();
  const { tunnel, setTunnel } = useTunnelData();

  const regulars = Object.values(tunnel.regulars).filter((e) => e.count > 0);
  const rp = tunnel.sales["_products"];

  const display = {
    regulars: regulars.map(({ count, regular }) => {
      return {
        id: regular["@id"],
        count: count,
        name: regular.name,
        article: rp.find((r) => r.offerId === regular.offer["@id"]),
      };
    }),
  };

  const togglePromoOpen = () => {
    setPromoIsPromo((prev) => !prev);
  };

  const toggleIsOpen = () => {
    setIsPromo((prev) => !prev);
  };

  const total = tunnel.sales.totalTI;

  const _onSubmit = () => {
    errorSet(false);
    codePendingSet(true);
    const [query] = Api.GET({
      path: `/api/abo/resamania2/promo/${code}`,
      query: { msdsId: tunnel.club.msdsId, tunnelUid: tunnel.uid, saleId: tunnel.sales["@id"] },
    });
    query
      .then((json) => {
        const [addQuery] = Api.POST({
          path: `/api/abo/resamania2/promo/${code}`,
          body: { saleId: tunnel.sales["@id"], msdsId: tunnel.club.msdsId, tunnelUid: tunnel.uid },
        });
        codePendingSet(false);
        return addQuery
          .then(() => {
            const [refreshQuery] = Api.GET({
              path: `/api/abo/resamania2/sales`,
              query: { msdsId: tunnel.club.msdsId, saleId: tunnel.sales["@id"] },
            });

            return refreshQuery
              .then((result) => {
                console.log('[SubscriptionSummary] Refresh result:', result);
                if (result && result.sale) {
                  setTunnel(prevTunnel => ({ ...prevTunnel, sales: result.sale, promo: json }));
                } else {
                  codeChanged(json);
                }
              })
              .catch((err) => {
                console.error(err.userMsg || "Une erreur est survenue");
                errorSet(err.userMsg || "Une erreur est survenue");
              });
          })
          .catch((err) => {
            console.error(err.userMsg || "Une erreur est survenue");
            errorSet(err.userMsg || "Une erreur est survenue");
          });
      })
      .catch((err) => {
        codePendingSet(false);
        errorSet(err.userMsg || "Une erreur est survenue");
      });
  };
  const codeChanged = (code) => {
    if (code) {
      setTunnel({ ...tunnel, promo: code });
    } else {
      setTunnel({ ...tunnel, promo: undefined });
    }
  };

  const codeChange = (e) => {
    errorSet(null);
    codeSet(e.target.value.trim().toUpperCase());
  };

  const showText = useMemo(() => {
    if (configCode === "onair.es" || configCode === "onair.test") return true;
    return false;
  }, [configCode]);

  return (
    <div
      className={cn(
        "w-full sm:w-4/5 lg:w-2/3 relative sm:my-2 xl:w-[27%] xl:absolute xl:top-1/4 xl:right-[1%] 2xl:-right-[-1%]",
        className
      )}
      style={{ margin: "20px auto", ...style }}
    >
      <div
        className={`w-full h-20 flex items-center justify-between px-4  cursor-pointer ${
          isOpen ? "text-oaForegroundText bg-[#efefef] brightness-95" : "text-white bg-oaRed rounded-[4px]"
        } transition-all duration-300`}
        onClick={toggleIsOpen}
      >
        <div className="flex gap-2  items-center">
          <ChevronDown className={`size-8 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
          <p className="text-2xl font-bold uppercase">{t("subscription.my_cart")}</p>
        </div>
        {!showText && (
          <div className="text-2xl font-bold animate-fade-in-up">
            {(total / 100).toFixed(2)} {currency}
          </div>
        )}
      </div>
      <section
        className={`w-full  bg-white  border-t sm:border-x md:border-[#E0E0E0] bottom-20  transition-all duration-300 rounded-t-xl ${
          isOpen ? " " : "h-0 overflow-hidden"
        }`}
      >
        <article className="w-full flex flex-col items-center gap-4 ">
          <section className="w-full bg-[#efefef] brightness-95 px-5 pb-4 pt-2 md:px-10 md:pb-6 md:pt-2 xl:px-5 border-b border-oaForegroundText">
            {!showText && (
              <>
                <div className="text-xl font-semibold">{t("subscription.summary").toUpperCase()}</div>
                <p className="text-xs font-semibold text-oaBackgroundText">{t("subscription.my_club").toUpperCase()}</p>
              </>
            )}
            <h3 className="text-base  font-extrabold">{club.name}</h3>
          </section>
          <section className="w-full md:flex-1 pb-4 bg-white border-b border-oaBorderColor px-5 xl:px-5 md:px-10  border-b border-oaForegroundText">
            {!showText && (
              <div className="flex justify-between">
                <p
                  className="font-bold text-nowrap"
                  style={{ justifyContent: "space-between", display: "flex", flexGrow: 1 }}
                >
                  <span>{t("subscription.registration_fees").toUpperCase()}</span>
                  {tunnel.offer.offer.registrationFeeTI === 0 && (
                    <span className="text-oaRed">{t("subscription.free").toUpperCase()}</span>
                  )}
                </p>
                {tunnel.offer.offer.registrationFeeTI !== 0 && (
                  <p className="font-normal text-nowrap">
                    {(tunnel.offer.offer.registrationFeeTI / 100).toFixed(2)} {currency}
                  </p>
                )}
              </div>
            )}
            <div className="flex justify-between mt-3 flex-nowrap">
              <p className="font-bold text-nowrap">{t("subscription.my_registration").toUpperCase()}</p>
              {!showText ? (
                <p className="font-normal text-wrap text-right">
                  {((tunnel.sales.totalTI - tunnel.offer.offer.registrationFeeTI) / 100).toFixed(2) + currency}
                </p>
              ) : (
                <p className="text-[10px] text-oaRed font-bold mt-1 text-nowrap">{tunnel.offer.d.name}</p>
              )}
            </div>
            {showText && <p className="font-normal text-wrap">{tunnel.offer.d.price_4}</p>}
            {!showText && <p className="text-[10px] text-oaRed font-bold mt-1 text-nowrap">{tunnel.offer.d.name}</p>}
            {showText && <p className="text-[12px] text-oaPlaceholderText mt-1 text-wrap">{tunnel.offer.d.txt}</p>}
            <p className="text-[10px] text-oaPlaceholderText font-light mt-1 text-nowrap">{tunnel.offer.d.full}</p>
          </section>
          <section className="mt-1 w-full px-5 md:px-10 xl:px-5" style={{ marginTop: "1rem" }}>
            {showText && (
              <div className="flex justify-between">
                <p className="text-nowrap" style={{ justifyContent: "space-between", display: "flex", flexGrow: 1 }}>
                  <span>{t("subscription.registration_fees").toUpperCase()}</span>
                  {tunnel.offer.offer.registrationFeeTI === 0 && (
                    <span className="text-oaRed">{t("subscription.free").toUpperCase()}</span>
                  )}
                </p>
                {tunnel.offer.offer.registrationFeeTI !== 0 && (
                  <p className="font-normal text-nowrap">
                    {(tunnel.offer.offer.registrationFeeTI / 100).toFixed(2)} {currency}
                  </p>
                )}
              </div>
            )}
            {!!display.regulars.length &&
              display.regulars.map((o, i) => {
                return (
                  <p key={i} className="flex justify-between">
                    <span className="col-span-8">{o.name}</span>
                    <span className="col-span-4 text-right">
                      {o.article && `${((o.count * o.article.totalTI) / 100).toFixed(2)} €`}
                    </span>
                  </p>
                );
              })}

            {showText && (
              <div className="flex justify-between mt-3 pb-3">
                <p className="text-xl font-bold animate-fade-in-up">{t("subscription.total_cart")}</p>
                <p className="text-xl font-bold animate-fade-in-up">
                  {(total / 100).toFixed(2)} {currency}
                </p>
              </div>
            )}
          </section>
          {!showText && (
            <section className="w-full flex flex-col gap-2  md:flex-1 pb-4 border-b border-oaBorderColor px-5 xl:px-5 md:px-10">
              <div className="flex gap-1 items-center">
                <Info className="size-5" />
                <h3 className="font-extrabold">{t("subscription.information").toUpperCase()}</h3>
              </div>
              <div>
                <h4 className="font-semibold">{t("subscription.material_warranty").toUpperCase()}</h4>
                <p className="text-xs font-light italic">{t("subscription.material_info", { price: "10.00" })}</p>
              </div>
              <div>
                <h4 className="font-semibold">{t("subscription.deposit").toUpperCase()}</h4>
                <p className="text-xs font-light italic">{t("subscription.deposit_info", { price: "50" })}</p>
              </div>
            </section>
          )}
        </article>
        {!tunnel.promo && !hidePromo && (
          <div className="">
            <div
              className={`flex gap-2  items-center py-4 cursor-pointer px-5 xl:px-5 md:px-10 ${
                promoIsOpen ? "bg-white text-oaForegroundText" : "bg-oaRed text-white"
              } transition-all duration-200`}
              onClick={togglePromoOpen}
            >
              <p className="text-sm font-semibold border-b border-white">{t("subscription.promo_code")}</p>
              <ChevronUp className={`size-6 transition-transform duration-200 ${promoIsOpen ? "" : "rotate-180"}`} />
            </div>

            <div className={`pb-3 px-5 md:px-10 ${promoIsOpen ? "" : "hidden"} overflow-hidden`}>
              {codePending && <p>...</p>}
              {!codePending && (
                <input
                  type="text"
                  name="promo-code"
                  className="border-2 border-oaForegroundText text-sm font-bold py-2 px-5 placeholder:text-oaForegroundText"
                  placeholder={t("subscription.promo_placeholder")}
                  value={code}
                  onChange={codeChange}
                />
              )}
              {!!error && (
                <>
                  <div style={{ display: "flex", gap: "0.2rem", margin: "1rem" }}>
                    <CircleAlert className="size-4 " />
                    <p className="text-xs flex-1">{error}</p>
                  </div>
                </>
              )}
              {!codePending && (
                <OAButton
                  className={"h-8 text-base w-[250px] mt-2 font-bold"}
                  onClick={_onSubmit}
                  value={code}
                  onChange={(e) => {
                    errorSet("");
                    codeSet(e.target.value.trim().toUpperCase());
                  }}
                >
                  {t("subscription.validate_code")}
                </OAButton>
              )}
            </div>
          </div>
        )}
        {!!tunnel.promo && (
          <section className="w-full bg-[#efefef] px-5 py-4 md:px-10 md:py-6 xl:px-5 border-b border-oaForegroundText">
            <div className={`text-xl font-semibold`}>Code: {tunnel.promo.code}</div>
          </section>
        )}
      </section>
    </div>
  );
}
